import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BackgroundImageCst from "@components/BackgroundImageCst";
import { Container } from "@mui/material";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { downloadFile, uploadFile } from "@utils/utilsApi";
import { ActionsType } from "src/interfaces/Common";
import { KeyURL, MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import ButtonBackPage from "@components/ButtonBackPage";
import HeroProject from "../HeroProject";
import Breadcrumb from "@components/Breadcrumb";

const GoodsReceipts: React.FC = () => {
  const [idGoodReceiptDeleted, setIdGoodReceiptDeleted] = useState<string>();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const { idProject, id, idCostModel, idCostModelDetail } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["projects.cost-goods-receipts"]}`;
  const { t } = useTranslation(["goods-receipts-page"]);
  const { t: tOptions } = useTranslation(["input"]);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  /*#######################################*/

  /*Permission to indetify PM*/
  const isPM = user?.permissions.find(
    (permission: any) =>
      permission === "usermgmt.budget.project.mark.as.pending.activation",
  );

  /*#######################################*/

  /*Get the project information*/
  const { dataDetail: dataProject, mutate: projectMutate } = useFetchDataDetail(
    `${ServicesURL.getProjects}/${idProject}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getGoodsReceiptsByCost.replaceAll(":costId", String(idCostModelDetail))}?corporateId=${id}`,
      PagesID["projects.cost-goods-receipts"],
      filters[filterId],
    );

  /*Formatting the data from server*/
  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      if (el.customer) {
        el.customer.label = el.customer?.business_name || "-";
      }

      return el;
    });
  }, [dataTable?.output]);

  /*Trigger to delete a good receipr*/
  const { trigger: triggerDeleteGoodReceipt } = useSWRMutation(
    `${ServicesURL.postGoodsReceipts.replaceAll(":costId", String(idCostModelDetail))}/${idGoodReceiptDeleted}?corporateId=${id}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  /*Function to handle the delete acition*/
  const handleDelete = React.useCallback(async () => {
    if (idGoodReceiptDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteGoodReceipt();
        const newData = [
          ...dataTable.output.filter(
            (el: any) => el.id !== idGoodReceiptDeleted,
          ),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdGoodReceiptDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idGoodReceiptDeleted, mutate, triggerDeleteGoodReceipt]);

  useEffect(() => {
    if (!idGoodReceiptDeleted) return;
  }, [idGoodReceiptDeleted]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns.map((el) => {
        return { ...el };
      });
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => {
              setIdGoodReceiptDeleted(id);
              setOpenModalDelete(true);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow, navigate]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    configRowNew = configRowNew.map((el: ActionsType) => {
      /*Disable delete if not deletable*/
      if (el.type === "delete" && !row.original?.deletable) {
        return { ...el, disabled: true };
      }

      if (
        el.type === "delete" &&
        isPM &&
        row.original?.status !== "REQUESTED"
      ) {
        return { ...el, disabled: true };
      }

      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "add":
          return {
            ...action,
            disabled: !!(isPM && dataProject?.dto?.status === "CLOSED"),
          };
        case "upload": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/download${
                    el ? `/${el}` : ""
                  }?corporate_id=1&type=LEARNER` || "",
                ),
              onUpload: (formData: FormData) => {
                return uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                );
              },
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        case "visibility": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${
                ServicesURL[action.service as KeyURL]
              }?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/?corporate_id=1` ||
                    "",
                ),
              onUpload: (formData: FormData) =>
                uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                ),
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar]);

  const renderCell = (cel: any, value: any, row: any) => {
    return value
      ? tOptions("options.switch_key.true")
      : tOptions("options.switch_key.false");
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-project.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth="xxl"
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroProject />

        <ButtonBackPage
          customNavigate={() =>
            navigate(
              `/corporate/${id}/detail/budget-management/projects/edit/${idProject}/cost-model/${idCostModel}/cost`,
            )
          }
        />
        <HeroCst title={t("hero-title")} description={""} />
        <Table
          id={PagesID["projects.cost-goods-receipts"]}
          data={dataFormat || []}
          columns={filteredColumns || []}
          configurationTopToolbar={actionsToolbar}
          numberOfActions={configurationActionForRow?.length}
          renderRowActions={customRenderRowActions}
          totalPages={dataTable?.metadata?.total_elements}
          modalTitle={t("hero-title")?.replace("-", "") || ""}
          renderCell={renderCell}
        />
        <ModalConfirmsCst
          open={!!openModalDelete}
          title={t("delete-item", { ns: "modal" })}
          description={t("confirm-delete", { ns: "modal" })}
          routeToSamePage={true}
          onCallBackClosed={() => {
            setOpenModalDelete(!openModalDelete);
            setOpenToast(undefined);
          }}
          onActionConfirmed={() => handleDelete()}
          status={openToast}
        />
        <ModalDownloadUploadCst
          open={!!openUpload}
          setOpen={() => {
            setOpenUpload(undefined);
          }}
          title={openUpload?.labelTranslate || ""}
          data={openUpload}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default GoodsReceipts;
